import { useCurrentPageAccess } from "@/app/router";
import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { SearchableSelect } from "@/shared/components/ui/searchable-select";
import { OffboardingConfirmDialog } from "../components/OffboardingConfirmDialog";
import { useOrganizationOffboarding } from "../hooks/useOrganizationOffboarding";
import type { StartOffboardingDto } from "../types/offboarding-types";

export function OrganizationOffboardingPage() {
  const vm = useOrganizationOffboarding();
  const { canWriteCurrentPage } = useCurrentPageAccess();
  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="Offboarding"
        description="선택한 조직을 프리즘에서 오프보딩 합니다. RabbitMQ 바인딩을 삭제합니다."
      />

      {vm.step === "intro" && (
        <Card className="p-6 space-y-4">
          <div className="whitespace-pre-wrap text-sm text-muted-foreground">
            {`선택한 조직을 프리즘에서 오프보딩 합니다.

- RabbitMQ 큐에 연결된 해당 조직에 대한 바인딩 삭제`}
          </div>

          <Button onClick={() => vm.setStep("user_input")} disabled={!canWriteCurrentPage}>
            시작하기
          </Button>
        </Card>
      )}

      {vm.step === "user_input" && (
        <Card className="p-6 space-y-6">
          <div className="space-y-2">
            <div className="text-sm font-medium">오프보딩 할 조직</div>
            <SearchableSelect
              className="w-[320px]"
              value={vm.form.org_name}
              onValueChange={(v) => vm.setForm((p: StartOffboardingDto) => ({ ...p, org_name: v }))}
              options={vm.orgList.map((o: string) => ({ value: o, label: o }))}
              placeholder="조직을 선택하세요"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={() => {
                const res = vm.openConfirm();
                if (!res.ok) alert(res.error);
              }}
            >
              오프보딩 실행
            </Button>
            <Button variant="outline" onClick={() => vm.setStep("intro")}>
              취소
            </Button>
          </div>

          <OffboardingConfirmDialog
            open={vm.confirmOpen}
            onOpenChange={vm.setConfirmOpen}
            orgName={vm.form.org_name}
            value={vm.userConfirmText}
            onChange={vm.setUserConfirmText}
            canConfirm={vm.isConfirmed}
            onConfirm={vm.start}
          />
        </Card>
      )}

      {vm.step === "offboarding" && (
        <Card className="p-6 flex items-center gap-3">
          <LoadingSpinner size="sm" />
          <span className="text-sm font-medium">오프보딩을 진행하고 있습니다.</span>
        </Card>
      )}

      {vm.step === "done" && (
        <Card className="p-6 space-y-4">
          {vm.error ? (
            <div className="text-sm font-medium text-destructive">오프보딩에 실패했습니다: {vm.error}</div>
          ) : (
            <div className="text-sm font-medium text-emerald-600">오프보딩이 완료되었습니다.</div>
          )}
          <Button onClick={vm.finish}>확인</Button>
        </Card>
      )}
    </div>
  );
}
